import React from 'react';
import { Check, X, Mail, UserCheck, Users } from 'lucide-react';
import { PreApprovalRecipient } from '@/types/pre-approval';
import { formatDistanceToNow } from 'date-fns';
import { formatPhoneNumber } from '@/utils/formatCurrency';

interface RecipientStatusListProps {
  recipients: PreApprovalRecipient[];
}

export const RecipientStatusList: React.FC<RecipientStatusListProps> = ({ recipients }) => {
  if (!recipients || recipients.length === 0) {
    return null;
  }

  const getRecipientTypeLabel = (type: string): string => {
    switch (type) {
      case 'borrower':
        return 'Borrower';
      case 'co_borrower':
        return 'Co-Borrower';
      case 'partner':
        return 'Partner';
      default:
        return 'Recipient';
    }
  };

  const getRecipientIcon = (type: string) => {
    switch (type) {
      case 'borrower':
        return <UserCheck className="h-4 w-4" />;
      case 'co_borrower':
        return <Users className="h-4 w-4" />;
      case 'partner':
        return <Mail className="h-4 w-4" />;
      default:
        return <Mail className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-gray-700">Recipients</h4>
      <div className="space-y-2">
        {recipients.map((recipient, index) => {
          const hasViewed = !!recipient.firstViewedAt;
          const viewCount = recipient.viewCount || 0;

          return (
            <div
              key={index}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                hasViewed
                  ? 'bg-green-50 border-green-200'
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div
                  className={`p-2 rounded-full ${
                    hasViewed ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {getRecipientIcon(recipient.type)}
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">
                      {recipient.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({getRecipientTypeLabel(recipient.type)})
                    </span>
                  </div>
                  <div className="text-xs text-gray-600">
                    {recipient.email || (recipient.phone ? formatPhoneNumber(recipient.phone) : '')}
                  </div>
                  {hasViewed && recipient.lastViewedAt && (
                    <div className="text-xs text-gray-500 mt-1">
                      {viewCount > 1 ? (
                        <>
                          Viewed {viewCount} times · Last{' '}
                          {formatDistanceToNow(new Date(recipient.lastViewedAt), {
                            addSuffix: true
                          })}
                        </>
                      ) : (
                        <>
                          Viewed{' '}
                          {formatDistanceToNow(new Date(recipient.firstViewedAt!), {
                            addSuffix: true
                          })}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {hasViewed ? (
                  <div className="flex items-center space-x-1 text-green-700">
                    <Check className="h-4 w-4" />
                    <span className="text-xs font-medium">Viewed</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1 text-gray-500">
                    <X className="h-4 w-4" />
                    <span className="text-xs font-medium">Not Viewed</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
